import { type RefObject, useEffect, useRef, useState } from "react";

interface FitTabLabels {
	/** Attach to the MUI Tabs root (must use variant="scrollable"). */
	ref: RefObject<HTMLDivElement | null>;
	/** True while the labels actually fit; false → show icons only. */
	showLabels: boolean;
}

/**
 * Show tab labels while they fit, collapse to icons only when they genuinely
 * overflow. Measures the real content width of the Tabs scroller (scrollWidth vs
 * clientWidth) — not a per-tab guess — and remembers the width the labels needed,
 * so re-expanding requires more room than the collapse point. That hysteresis
 * prevents oscillation. `tabCount` re-measures when the tab set changes.
 *
 * Requires the Tabs to use variant="scrollable" so `.MuiTabs-scroller` exists
 * (which also gives a scroll fallback if even the icons overflow).
 */
export const useFitTabLabels = (tabCount: number): FitTabLabels => {
	const ref = useRef<HTMLDivElement>(null);
	const [showLabels, setShowLabels] = useState(true);
	// Content width the labelled tabs needed last time they were measured.
	const labeledWidthRef = useRef(0);

	// Re-measure from scratch when the tab set changes.
	useEffect(() => {
		labeledWidthRef.current = 0;
		setShowLabels(true);
	}, [tabCount]);

	// Re-runs after every toggle (showLabels dep) so it always reads the DOM that
	// matches the current label state.
	useEffect(() => {
		const root = ref.current;
		if (!root) return;
		const measure = () => {
			const scroller = root.querySelector<HTMLElement>(".MuiTabs-scroller");
			if (!scroller) return;
			if (showLabels) {
				// Labels rendered: collapse only if they overflow.
				if (scroller.scrollWidth > scroller.clientWidth + 1) {
					labeledWidthRef.current = scroller.scrollWidth;
					setShowLabels(false);
				}
			} else if (
				// Icons rendered: re-expand only when labels would genuinely fit again.
				labeledWidthRef.current > 0 &&
				scroller.clientWidth >= labeledWidthRef.current + 2
			) {
				setShowLabels(true);
			}
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(root);
		return () => observer.disconnect();
	}, [showLabels, tabCount]);

	return { ref, showLabels };
};
